import { JSONContent } from '@tiptap/core';

import { ReasonToken, Recipient, UserRecipientAPIData } from '@bonusly/recipient-entities';

import { LexerToken } from './rich-text-lexer';

type Bonus = {
	receivers: UserRecipientAPIData[];
	reason_tokens?: ReasonToken[];
};

const parseTextToken = (source: string): JSONContent[] => {
	if (!source) {
		return [];
	}

	const nodes: JSONContent[] = [];
	const pushText = (text: string) => {
		if (text) {
			nodes.push({
				type: 'text',
				text,
			});
		}
	};

	let cursor = 0;

	while (cursor < source.length) {
		const openBracket = source.indexOf('[', cursor);
		if (openBracket === -1) {
			pushText(source.slice(cursor));
			break;
		}

		pushText(source.slice(cursor, openBracket));

		// Keep image markdown syntax as plain text: ![alt](url)
		if (openBracket > 0 && source[openBracket - 1] === '!') {
			pushText('[');
			cursor = openBracket + 1;
			continue;
		}

		const closeBracket = source.indexOf(']', openBracket + 1);
		if (closeBracket === -1 || source[closeBracket + 1] !== '(') {
			pushText('[');
			cursor = openBracket + 1;
			continue;
		}

		const linkText = source.slice(openBracket + 1, closeBracket);
		const urlStart = closeBracket + 2;
		let urlEnd = urlStart;
		let depth = 1;

		while (urlEnd < source.length && depth > 0) {
			const char = source[urlEnd];
			if (char === '(') depth += 1;
			if (char === ')') depth -= 1;
			urlEnd += 1;
		}

		if (depth !== 0) {
			pushText('[');
			cursor = openBracket + 1;
			continue;
		}

		const href = source.slice(urlStart, urlEnd - 1);
		if (!linkText || !href) {
			pushText(source.slice(openBracket, urlEnd));
			cursor = urlEnd;
			continue;
		}

		nodes.push({
			type: 'text',
			text: linkText,
			marks: [
				{
					type: 'link',
					attrs: {
						href,
					},
				},
			],
		});
		cursor = urlEnd;
	}

	return nodes;
};

export class RichTextParser {
	constructor(
		public readonly tokens: LexerToken[] = [],
		private bonus?: Bonus
	) {}

	content(): JSONContent[] {
		return this.tokens.flatMap((token) => {
			if (token.type === 'text') {
				return parseTextToken(token.value);
			}

			if (token.type === 'recipient' && this.bonus && this.bonus.reason_tokens) {
				const reasonToken = this.bonus.reason_tokens.find((t: any) => t.token.substring(1) === token.value);
				if (reasonToken) {
					return [
						{
						type: token.type,
						attrs: {
							id: token.value,
							record: Recipient.fromReasonToken(reasonToken, this.bonus.receivers),
						},
						},
					];
				}
			}

			return [
				{
					type: token.type,
					attrs: {
						id: token.value,
					},
				},
			];
		});
	}

	document(): JSONContent {
		return {
			type: 'doc',
			content: [
				{
					type: 'paragraph',
					content: this.content(),
				},
			],
		};
	}
}
